/**
 * GBUX play-ledger math SSOT — tables, slots, deposits, purchases.
 *
 * ## Product law
 * **Play GBUX = real GBUX spendable on poker.grudge-studio.com** (dApp ledger).
 * “Play” means in-play on this product — **not** fake/demo chips.
 *
 * ## Funding real play GBUX
 * - Third-party wallet SPL → BUDBai agent → play **1:1**
 * - Purchase SOL/USDT (or USD via agent) @ **$0.001 USD = 1 GBUX**
 * - AI agent / house systems (BJ bank, AI stacks, rake) move real D1 GBUX
 *
 * ## Pricing contract
 * **$0.001 USD = 1 GBUX** always.
 * - 1 USDT (≈ $1) → 1_000 GBUX
 * - N SOL → floor(N × solPriceUsd / 0.001) GBUX
 * - SPL GBUX deposit → play 1:1 (not USD-priced)
 *
 * Purchase fee: **5%** of USD notional (platform fee) before GBUX mint-to-play.
 * Optional dapp fee-payer (gas) is separate — see /api/wallet/presign-tx.
 * Amounts on the play ledger are whole GBUX units (UI integers / 2dp display).
 */

/** On-chain SPL decimals for GBUX mint */
export const GBUX_DECIMALS = 9;

/**
 * List price: USD per 1 GBUX.
 * Contract: rateUsd × gbux = usdValue  ⇒  gbux = usdValue / rateUsd
 */
export const GBUX_USD_RATE = 0.001;

/** Alias used in fleet / UI */
export const GBUX_RATE_USD = GBUX_USD_RATE;

/**
 * Platform fee as fraction of USD notional before GBUX credit.
 * 0.05 = 5% (dapp / product fee; gas fee-payer is separate).
 */
export const GBUX_PURCHASE_FEE = 0.05;

/** Round to play ledger precision (2 dp max, non-negative) */
export function roundPlayGbux(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100) / 100;
}

/** Floor to whole GBUX for SPL deposit / purchase credits */
export function floorPlayGbux(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

/** Human amount → raw SPL units */
export function toGbuxRaw(amountHuman: number, decimals = GBUX_DECIMALS): bigint {
  const safe = Math.max(0, amountHuman);
  const factor = 10 ** decimals;
  return BigInt(Math.floor(safe * factor + 1e-9));
}

export function fromGbuxRaw(raw: bigint | number, decimals = GBUX_DECIMALS): number {
  const n = typeof raw === "bigint" ? Number(raw) : raw;
  if (!Number.isFinite(n)) return 0;
  return n / 10 ** decimals;
}

export function formatPlayGbux(n: number, opts?: { compact?: boolean }): string {
  const v = roundPlayGbux(Number(n) || 0);
  if (opts?.compact) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 10_000) return `${(v / 1000).toFixed(1)}K`;
  }
  return v.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

/** Total bet for slots / lines */
export function totalLineBet(betPerLine: number, lines: number): number {
  return roundPlayGbux(betPerLine * lines);
}

/** USD notional → play GBUX at $0.001 / GBUX (optional fee) */
export function usdToGbux(
  usd: number,
  feeFraction: number = GBUX_PURCHASE_FEE,
): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0;
  const fee = Math.max(0, Math.min(0.5, feeFraction));
  const net = usd * (1 - fee);
  return floorPlayGbux(net / GBUX_USD_RATE);
}

/** SOL amount × USD price → play GBUX ($0.001 = 1 GBUX) */
export function solToGbux(
  solAmount: number,
  solPriceUsd: number,
  feeFraction: number = GBUX_PURCHASE_FEE,
): number {
  if (!Number.isFinite(solAmount) || solAmount <= 0) return 0;
  const px = Number.isFinite(solPriceUsd) && solPriceUsd > 0 ? solPriceUsd : 150;
  return usdToGbux(solAmount * px, feeFraction);
}

/** USDT amount (≈ USD) → play GBUX */
export function usdtToGbux(
  usdtAmount: number,
  feeFraction: number = GBUX_PURCHASE_FEE,
): number {
  return usdToGbux(usdtAmount, feeFraction);
}

/** Inverse: GBUX → USD list value */
export function gbuxToUsd(gbux: number): number {
  if (!Number.isFinite(gbux) || gbux <= 0) return 0;
  return gbux * GBUX_USD_RATE;
}
